import { useMemo } from 'react';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import { useFormContext } from 'react-hook-form-mui';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import IconImg from 'src/assets/images/icons/analytic-landing-icon.svg';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';
import { useChartReports } from '../analytics/useAnalytics';
import { useDeleteReportHandler } from './useDeleteReportHandler';
import { MultipleSelectDropdown } from '../analytics/filter/MultipleSelectDropdown';
import { ReportCard } from './ReportCard';
import { CreateNewReport } from './CreateNewReport';
import { LandingSection } from './Section';
import { LandingAnalyticsProps } from './types';

export interface ChartReport {
  name: string;
  hashFilters: string;
}

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '180px',
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    padding: '8px 16px',
  },
  [theme.breakpoints.down(1520)]: {
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '6px 12px',
    },
  },
}));

const sortOptions = [
  {
    label: 'Newest',
    value: 'desc',
  },
  {
    label: 'Oldest',
    value: 'asc',
  },
];

export const LandingAnalytics = ({ onRefresh, isLoading: externalLoading }: LandingAnalyticsProps) => {
  const { watch, control } = useFormContext();
  const sort = watch('analyticSort');
  const { data: dataAnalytics, isLoading } = useChartReports('REPORT_ANALYTIC');
  const { deleteDialogOpen, handleDeleteClick, handleConfirmDelete, handleCancelDelete } = useDeleteReportHandler();
  const listAnalytics = useMemo(() => {
    if (dataAnalytics) {
      const newItems = [...dataAnalytics];
      newItems.sort((a, b) => {
        const aDate = new Date(a.updateDate);
        const bDate = new Date(b.updateDate);

        const compareStatus = sort === 'desc' ? isBefore(aDate, bDate) : isAfter(aDate, bDate);
        return compareStatus ? 1 : -1;
      });
      return newItems;
    }
    return [];
  }, [dataAnalytics, sort]);

  return (
    <LandingSection
      icon={IconImg}
      title='Reports & Analytics'
      linkDetail='/analytics'
      isLoading={isLoading || externalLoading}
      skeletonType='cards'
      onRefresh={onRefresh}
      topRight={
        <StyledBox>
          <MultipleSelectDropdown name='analyticSort' label='Sort by Date' control={control} options={sortOptions} multiple={false} />
        </StyledBox>
      }
    >
      <div className='-mx-8 xl:-mx-12 flex flex-wrap'>
        <CreateNewReport />
        {listAnalytics?.map((item) => (
          <div key={`card-audit-${item?.id}`} className='px-8 xl:px-12 w-1/4 mb-16 xl:mb-30  min-h-[110px] xl:min-h-[8.4375rem]'>
            <ReportCard item={item} rootPath='/analytics' onDelete={() => handleDeleteClick(item.id)} />
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <ModalConfirm open={deleteDialogOpen} title='Delete Report' onAccept={handleConfirmDelete} onCancel={handleCancelDelete} buttonAcceptLabel='Delete'>
        <p>Are you sure you want to delete this report? This action cannot be undone.</p>
      </ModalConfirm>
    </LandingSection>
  );
};
